import { UseQueryResult } from '@tanstack/react-query'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import { TIPI_INTERVENTO } from '../../constants'
import { useQsFilters } from '../../hooks/filters'
import { PaginatedDJResponse, Proposta } from '../../types'
import { proposteInitFilters } from './ProposteFilters'

interface ProposteTableInterface {
  id: Proposta['id']
  cliente_pk: Proposta['cliente_pk']
  cliente_ragione_sociale: Proposta['cliente_ragione_sociale']
  sede_data: {
    toponimo_data?: {
      nome: string
    }
    via: string
    civico: string
    comune_data?: {
      nome: string
    }
    provincia_data?: {
      sigla: string
    }
  }
  tipo_intervento: Proposta['tipo_intervento']
  data_creazione: Proposta['data_creazione']
  stato: Proposta['stato']
}

type PropostaTableProps = {
  proposte: UseQueryResult<PaginatedDJResponse<ProposteTableInterface>, Error>
}

export function ProposteTable({ proposte }: PropostaTableProps) {
  const { uiFilters, setFilters } = useQsFilters(proposteInitFilters)

  const [openMoreProposta, setOpenMoreProposta] = useState<number | null>(null)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.target instanceof Element && !e.target.closest(`#proposta-${openMoreProposta}`)) {
        setOpenMoreProposta(null)
      }
    }
    document.addEventListener('click', handleClick)
    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [openMoreProposta])
  return (
    <StickyTable isLoading={proposte.isLoading}>
      <thead className="position-sticky top-0 bg-white">
        <Sorter
          value={uiFilters.ordering}
          onSortChange={(ordering) =>
            setFilters({
              ordering,
            })
          }
        >
          <tr>
            <th>
              <div className="d-flex align-items-center">
                Id Proposta
                <SortControl field="id" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Cliente
                <SortControl field="cliente_ragione_sociale" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">Sede</div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Tipo intervento
                <SortControl field="tipo_intervento" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Data di invio
                <SortControl field="data_creazione" />
              </div>
            </th>
            <th>
              <div className="d-flex align-items-center">
                Stato
                <SortControl field="stato" />
              </div>
            </th>
            <th>Azioni</th>
          </tr>
        </Sorter>
      </thead>
      <tbody>
        {proposte?.data?.results.length === 0 && (
          <tr>
            <td colSpan={7} className="text-center">
              Nessuna proposta disponibile
            </td>
          </tr>
        )}
        {proposte?.data?.results?.map((proposta, i) => (
          <tr key={proposta.id}>
            <td>
              <Link className="link-md" to={`/proposte/${proposta.id}`}>
                {proposta.id.toString().padStart(5, '0')}
              </Link>
            </td>
            <td>
              <Link className="link-md" to={`/clienti/${proposta.cliente_pk}`}>
                {proposta.cliente_ragione_sociale}
              </Link>
            </td>
            <td>
              {proposta.sede_data.toponimo_data?.nome} {proposta.sede_data.via}, {proposta.sede_data.civico} -{' '}
              {proposta.sede_data.comune_data?.nome} ({proposta.sede_data.provincia_data?.sigla})
            </td>
            <td>
              <div
                className={classNames('badge w-max-content', {
                  'bg-rapporto-taratura-light': proposta.tipo_intervento === 'rapporto_taratura',
                  'bg-intervento-periodico-light': proposta.tipo_intervento === 'verifica_periodica',
                  'text-rapporto-taratura': proposta.tipo_intervento === 'rapporto_taratura',
                  'text-intervento-periodico': proposta.tipo_intervento === 'verifica_periodica',
                })}
                style={{ width: 'max-content' }}
              >
                {TIPI_INTERVENTO.find((t) => t.value === proposta.tipo_intervento)?.label ?? proposta.tipo_intervento}
              </div>
            </td>
            <td>{dayjs(proposta.data_creazione).format('DD/MM/YYYY')}</td>
            <td>
              <div
                className={classNames('badge text-capitalize', {
                  'badge-state-success': proposta.stato === 'accettata' || proposta.stato === 'prenotata',
                  'badge-state-all': proposta.stato === 'scaduta',
                  'badge-state-warning': proposta.stato === 'inviata' || proposta.stato === 'sollecitata',
                  'badge-state-danger': proposta.stato === 'rifiutata' || proposta.stato === 'annullata',
                })}
              >
                {proposta.stato}
              </div>
            </td>
            <td>
              {(proposta.stato === 'inviata' || proposta.stato === 'sollecitata' || proposta.stato === 'scaduta') && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (openMoreProposta === i) {
                      setOpenMoreProposta(null)
                    } else {
                      setOpenMoreProposta(i)
                    }
                  }}
                  variant="light"
                  className="text-secondary d-flex align-items-center"
                  type="button"
                >
                  <span className="material-icons">more_horiz</span>
                </Button>
              )}
              {openMoreProposta === i && (
                <div
                  id={`proposta-${i}`}
                  className="list-group position-absolute"
                  // ref={editDeleteRef}
                  style={{
                    // zIndex: 1,
                    width: 'max-content',
                    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
                    right: 20,
                    borderRadius: '5px',
                  }}
                >
                  <button
                    onClick={() => {
                      const wx = window.open(
                        `/form-proposta/${proposta.id}`,
                        'Compila proposta',
                        'height=500,width=800,resizable=yes,scrollbars=yes'
                      )
                      wx?.addEventListener('unload', () => {
                        proposte?.refetch()
                      })
                    }}
                    className="list-group-item list-group-item-action d-flex align-items-center"
                  >
                    <span
                      className="material-icons me-2"
                      style={{
                        fontSize: '16px',
                      }}
                    >
                      visibility
                    </span>
                    Compila
                  </button>
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </StickyTable>
  )
}
